/**
 * FundingTab — IN REVIEW stage
 * Grant tracking: source, amount, period, PI, funder requirements
 */

import { useState } from 'react'
import type { ProjectDetailProject, ProjectFunding } from '../../../types'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

export function FundingTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions: sc,
  tabId = 'tab-funding',
}: TabContentProps) {
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId, sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId, sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId, sectionId)

  const [grants, setGrants] = useState<ProjectFunding[]>(
    project.funding || []
  )

  const [newGrant, setNewGrant] = useState({
    source: '',
    amount: '',
    period: '',
  })

  const handleAddGrant = () => {
    if (newGrant.source.trim() && newGrant.amount.trim()) {
      const updated = [...grants, {
        id: Date.now().toString(),
        source: newGrant.source,
        amount: parseFloat(newGrant.amount),
        period: newGrant.period,
        status: 'Active',
      } as ProjectFunding]
      setGrants(updated)
      onFieldChange('funding', updated)
      setNewGrant({ source: '', amount: '', period: '' })
    }
  }

  const handleRemoveGrant = (grantId: string | number) => {
    const updated = grants.filter((g) => g.id !== grantId)
    setGrants(updated)
    onFieldChange('funding', updated)
  }

  const totalFunding = grants.reduce((sum, g) => sum + (g.amount || 0), 0)

  return (
    <div className="space-y-6">
      {/* Total Funding Summary */}
      <CompletableSection
        sectionId="fund-total"
        title="Total Funding"
        isComplete={isComplete('fund-total')}
        completedByName={getInfo('fund-total')?.completed_by_name}
        completedAt={getInfo('fund-total')?.completed_at}
        onToggle={() => toggle('fund-total')}
      >
        <div className="text-[24px] font-bold text-[#10B981]">
          ${totalFunding.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </div>
        <p className="text-[12px] text-[#a8a8c0] mt-1">
          From {grants.length} active grant(s)
        </p>
      </CompletableSection>

      {/* Active Grants */}
      <div className="space-y-3">
        {grants.length > 0 ? (
          grants.map((grant) => (
            <div
              key={grant.id}
              className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-[14px] font-semibold text-[#e8e8f0]">
                    {grant.source}
                  </h4>
                  <p className="text-[12px] text-[#a8a8c0] mt-1">
                    {grant.period || 'No period specified'}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveGrant(grant.id)}
                  className="
                    text-[12px] text-[#E61B25]
                    hover:text-[#d41620]
                    font-medium
                  "
                >
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3 text-[13px]">
                <div>
                  <label className="text-[11px] text-[#a8a8c0] block mb-1">
                    Amount
                  </label>
                  <div className="text-[#10B981] font-semibold">
                    ${grant.amount?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-[#a8a8c0] block mb-1">
                    PI
                  </label>
                  <div className="text-[#e8e8f0]">
                    {grant.pi_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-[#a8a8c0] block mb-1">
                    Status
                  </label>
                  <div className="text-[#e8e8f0] font-medium">
                    {grant.status || 'Active'}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="
            bg-[#1e1e38] border border-[#2a2a44]
            rounded-xl p-5
            text-center text-[13px] text-[#a8a8c0]
          ">
            No grants tracked yet
          </div>
        )}
      </div>

      {/* Add Grant — NOT wrapped in CompletableSection (action form) */}
      <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-5">
        <h3 className="text-[13px] font-medium text-[#e8e8f0] mb-4">
          Add Grant
        </h3>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Funding source (e.g. NIH, NSF, EU H2020)"
            value={newGrant.source}
            onChange={(e) =>
              setNewGrant({ ...newGrant, source: e.target.value })
            }
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
          <input
            type="number"
            placeholder="Amount in USD"
            value={newGrant.amount}
            onChange={(e) =>
              setNewGrant({ ...newGrant, amount: e.target.value })
            }
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
          <input
            type="text"
            placeholder="Grant period (e.g. 2024-2026)"
            value={newGrant.period}
            onChange={(e) =>
              setNewGrant({ ...newGrant, period: e.target.value })
            }
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
          <button
            onClick={handleAddGrant}
            className="
              w-full px-4 py-2
              bg-[#12122a] border border-dashed border-[#2a2a44]
              rounded-lg
              text-[13px] font-medium text-[#a8a8c0]
              hover:border-[#3a3a54] hover:text-[#e8e8f0]
              transition-colors
            "
          >
            + Add Grant
          </button>
        </div>
      </div>

      {/* Funder Requirements */}
      <CompletableSection
        sectionId="fund-requirements"
        title="Funder Requirements & Restrictions"
        isComplete={isComplete('fund-requirements')}
        completedByName={getInfo('fund-requirements')?.completed_by_name}
        completedAt={getInfo('fund-requirements')?.completed_at}
        onToggle={() => toggle('fund-requirements')}
      >
        <div className="text-[12px] text-[#a8a8c0] space-y-2">
          <p>
            <strong>IP Requirements:</strong> Typically require publications with acknowledgment
          </p>
          <p>
            <strong>Equity:</strong> Some funders require founder equity stake
          </p>
          <p>
            <strong>Reporting:</strong> Annual progress reports and financial audits
          </p>
        </div>
      </CompletableSection>
    </div>
  )
}

export default FundingTab
